import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import EntityBadge from '../EntityBadge.jsx';
import ServiceLineModal from './ServiceLineModal.jsx';
import { formatCurrency } from '../../lib/format.js';
import { useAddServiceLine, useUpdateLine, useDeleteLine } from '../../hooks/useIntercompany.js';

function EntityCell({ entityId, entityMap }) {
  const e = entityMap[entityId];
  return <EntityBadge slug={e?.slug} name={e?.name ?? entityId} />;
}

export default function ServicesSection({ lines = [], entityMap = {}, entities = [], yyyymm, isValidated }) {
  const serviceLines = lines.filter((l) => l.type === 'service');

  const addLine = useAddServiceLine();
  const updateLine = useUpdateLine();
  const deleteLine = useDeleteLine();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);

  function openAdd() {
    setEditingLine(null);
    setModalOpen(true);
  }

  function openEdit(line) {
    setEditingLine(line);
    setModalOpen(true);
  }

  async function handleSave(payload) {
    if (editingLine) {
      await updateLine.mutateAsync({ id: editingLine.id, ...payload });
    } else {
      await addLine.mutateAsync({ yyyymm, ...payload });
    }
    setModalOpen(false);
  }

  async function handleDelete(lineId) {
    if (!window.confirm('Supprimer cette ligne de prestation ?')) return;
    await deleteLine.mutateAsync(lineId);
  }

  const isMutating = addLine.isPending || updateLine.isPending;

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-slate-500">
          {serviceLines.length} prestation{serviceLines.length !== 1 ? 's' : ''}
        </span>
        {!isValidated && (
          <button
            type="button"
            onClick={openAdd}
            className="flex items-center gap-1.5 rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 transition-colors"
            aria-label="Ajouter une prestation"
          >
            <Plus size={13} aria-hidden />
            Ajouter une prestation
          </button>
        )}
      </div>

      {serviceLines.length === 0 ? (
        <p className="text-sm text-slate-500 py-4 text-center">
          Aucune prestation de service saisie pour cette période.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 sticky left-0 bg-slate-50 whitespace-nowrap">
                  Émettrice
                </th>
                <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                  Débitrice
                </th>
                <th className="text-left text-xs font-medium text-slate-500 px-3 py-2.5 min-w-[140px]">
                  Label
                </th>
                <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                  HT
                </th>
                <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap hidden md:table-cell">
                  TVA %
                </th>
                <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap hidden md:table-cell">
                  TVA
                </th>
                <th className="text-right text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                  TTC
                </th>
                <th className="text-center text-xs font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {serviceLines.map((line) => (
                <tr key={line.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2.5 sticky left-0 bg-white hover:bg-slate-50">
                    <EntityCell entityId={line.from_entity_id} entityMap={entityMap} />
                  </td>
                  <td className="px-3 py-2.5">
                    <EntityCell entityId={line.to_entity_id} entityMap={entityMap} />
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 text-xs min-w-[140px]">
                    {line.label ?? <span className="text-slate-400 italic">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap font-medium">
                    {formatCurrency(line.amount_ht_cents)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 whitespace-nowrap hidden md:table-cell">
                    {line.vat_rate != null ? `${line.vat_rate} %` : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-500 whitespace-nowrap hidden md:table-cell">
                    {formatCurrency(line.vat_cents)}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-900 whitespace-nowrap font-semibold">
                    {formatCurrency(line.total_ttc_cents)}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(line)}
                        disabled={isValidated}
                        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
                        aria-label="Modifier la ligne"
                      >
                        <Pencil size={13} aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(line.id)}
                        disabled={isValidated}
                        className="rounded p-1 text-slate-400 hover:bg-red-100 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:outline-none"
                        aria-label="Supprimer la ligne"
                      >
                        <Trash2 size={13} aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ServiceLineModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        line={editingLine}
        entities={entities}
        isPending={isMutating}
      />
    </>
  );
}
